import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import socket from "../Sales1/socket";
import useIdleDetector from "./useIdleDetector";
import {
  addMessage,
  setUserTyping,
  clearUserTyping,
  setUserPresence,
  incrementUnread,
  updateReadReceipts,
  addConversation,
  fetchUnreadCount,
  fetchConversations,
  setActiveConversation,
  updateMessageReactions,
  updateMessageContent,
  markMessageDeleted,
  updateMessagePin,
  updateMessageAction,
  updateConversation,
  updateMessageDeliveryStatus,
  markMessagesReadThunk,
} from "../../../Slices/chatSlice";
import { message as antdMessage } from "antd";

export default function ChatSocketListener() {
  const dispatch = useDispatch();
  const activeConversationId = useSelector((state) => state.chat.activeConversationId);
  const activeConvRef = useRef(activeConversationId);
  const audioRef = useRef(null);
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);

  // Auto-away detection (5 min idle → away, activity → back to online)
  useIdleDetector(loginUser?.id);

  useEffect(() => {
    activeConvRef.current = activeConversationId;
  }, [activeConversationId]);

  // Request desktop notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (!loginUser?.id) return;

    // Fetch initial unread count
    dispatch(fetchUnreadCount(loginUser.id));

    const showDesktopNotification = (senderName, messageText, conversationId) => {
      if (!("Notification" in window) || Notification.permission !== "granted") return;
      const body = messageText?.substring(0, 100) || "Sent a file";
      const notification = new Notification(senderName || "New Message", {
        body,
        icon: "/logo192.png",
        tag: `chat-${conversationId}`,
        silent: true, // We play our own sound
      });
      notification.onclick = () => {
        window.focus();
        dispatch(setActiveConversation(conversationId));
        notification.close();
      };
      // Auto-close after 6 seconds
      setTimeout(() => notification.close(), 6000);
    };

    // Dedup set to prevent double-processing (message arrives via room + personal room)
    const processedMsgIds = new Set();

    const handleNewMessage = (data) => {
      // Skip if already processed (can arrive via both conversation room and personal room)
      if (data.id && processedMsgIds.has(data.id)) return;
      if (data.id) {
        processedMsgIds.add(data.id);
        // Keep set small — clear old entries after 30s
        setTimeout(() => processedMsgIds.delete(data.id), 30000);
      }

      dispatch(addMessage(data));

      // Send delivery acknowledgment for messages from others
      if (data.senderId !== loginUser.id && data.id) {
        socket.emit("chat:message-delivered", {
          messageId: data.id,
          conversationId: data.conversationId,
          userId: loginUser.id,
        });
      }

      // If this message IS in the active conversation and from another user, auto-mark as read
      if (data.conversationId === activeConvRef.current && data.senderId !== loginUser.id) {
        dispatch(markMessagesReadThunk({ conversationId: data.conversationId, userId: loginUser.id }));
      }

      // If this message is NOT in the active conversation, increment unread + notify
      if (data.conversationId !== activeConvRef.current && data.senderId !== loginUser.id) {
        dispatch(incrementUnread({ conversationId: data.conversationId }));
        // Play notification sound
        if (audioRef.current) {
          audioRef.current.play().catch(() => {});
        }
        // Desktop notification
        showDesktopNotification(data.senderName, data.content, data.conversationId);
        // In-app toast
        antdMessage.info({
          content: `${data.senderName}: ${data.content?.substring(0, 50) || "sent a file"}`,
          duration: 3,
        });
      }
    };

    const handleMessageStatusUpdate = ({ messageId, conversationId, userId, status }) => {
      dispatch(updateMessageDeliveryStatus({ conversationId, messageId, userId, status }));
    };

    const handleTyping = ({ conversationId, userId, userName }) => {
      if (userId === loginUser.id) return;
      dispatch(setUserTyping({ conversationId, userId, userName }));
    };

    const handleStopTyping = ({ conversationId, userId }) => {
      dispatch(clearUserTyping({ conversationId, userId }));
    };

    const handlePresenceUpdate = ({ userId, status, lastSeen, customStatus }) => {
      dispatch(setUserPresence({ userId, status, lastSeen, customStatus }));
    };

    const handleMessagesRead = ({ conversationId, userId, readAt }) => {
      dispatch(updateReadReceipts({ conversationId, userId, readAt }));
    };

    const handleConversationCreated = (conv) => {
      dispatch(addConversation(conv));
    };

    const handleUnreadUpdate = ({ conversationId }) => {
      if (conversationId !== activeConvRef.current) {
        dispatch(incrementUnread({ conversationId }));
      }
    };

    const handleReactionUpdate = ({ messageId, conversationId, reactions }) => {
      dispatch(updateMessageReactions({ conversationId, messageId, reactions }));
    };

    const handleMessageEdited = ({ messageId, conversationId, content, editedAt }) => {
      dispatch(updateMessageContent({ conversationId, messageId, content, editedAt }));
    };

    const handleMessageDeleted = ({ messageId, conversationId }) => {
      dispatch(markMessageDeleted({ conversationId, messageId }));
    };

    const handleMessagePinned = ({ messageId, conversationId, pinnedBy }) => {
      dispatch(updateMessagePin({ conversationId, messageId, pinnedBy }));
    };

    const handleMessageUnpinned = ({ messageId, conversationId }) => {
      dispatch(updateMessagePin({ conversationId, messageId, pinnedBy: null }));
    };

    const handleActionExecuted = ({ messageId, conversationId, actionType, result, resultMessage }) => {
      dispatch(updateMessageAction({ conversationId, messageId, result, resultMessage }));
    };

    const handleReminderDue = ({ reminderText, linkedEntity }) => {
      antdMessage.info({
        content: `⏰ Reminder: ${reminderText?.substring(0, 60) || 'You have a reminder'}`,
        duration: 8,
      });
    };

    const handleParticipantAdded = ({ conversationId, newParticipants }) => {
      dispatch(fetchConversations({ userId: loginUser.id }));
    };

    const handleParticipantRemoved = ({ conversationId, userId }) => {
      if (userId === loginUser.id) {
        dispatch(fetchConversations({ userId: loginUser.id }));
      } else {
        dispatch(fetchConversations({ userId: loginUser.id }));
      }
    };

    const handleGroupNameUpdated = ({ conversationId, name }) => {
      dispatch(updateConversation({ id: conversationId, name }));
    };

    socket.on("chat:new-message", handleNewMessage);
    socket.on("chat:user-typing", handleTyping);
    socket.on("chat:user-stop-typing", handleStopTyping);
    socket.on("chat:presence-update", handlePresenceUpdate);
    socket.on("chat:messages-read", handleMessagesRead);
    socket.on("chat:conversation-created", handleConversationCreated);
    socket.on("chat:unread-update", handleUnreadUpdate);
    socket.on("chat:reaction-update", handleReactionUpdate);
    socket.on("chat:message-edited", handleMessageEdited);
    socket.on("chat:message-deleted", handleMessageDeleted);
    socket.on("chat:message-pinned", handleMessagePinned);
    socket.on("chat:message-unpinned", handleMessageUnpinned);
    socket.on("chat:action-executed", handleActionExecuted);
    socket.on("chat:reminder-due", handleReminderDue);
    socket.on("chat:participant-added", handleParticipantAdded);
    socket.on("chat:participant-removed", handleParticipantRemoved);
    socket.on("chat:group-name-updated", handleGroupNameUpdated);
    socket.on("chat:message-status-update", handleMessageStatusUpdate);

    return () => {
      socket.off("chat:new-message", handleNewMessage);
      socket.off("chat:user-typing", handleTyping);
      socket.off("chat:user-stop-typing", handleStopTyping);
      socket.off("chat:presence-update", handlePresenceUpdate);
      socket.off("chat:messages-read", handleMessagesRead);
      socket.off("chat:conversation-created", handleConversationCreated);
      socket.off("chat:unread-update", handleUnreadUpdate);
      socket.off("chat:reaction-update", handleReactionUpdate);
      socket.off("chat:message-edited", handleMessageEdited);
      socket.off("chat:message-deleted", handleMessageDeleted);
      socket.off("chat:message-pinned", handleMessagePinned);
      socket.off("chat:message-unpinned", handleMessageUnpinned);
      socket.off("chat:action-executed", handleActionExecuted);
      socket.off("chat:reminder-due", handleReminderDue);
      socket.off("chat:participant-added", handleParticipantAdded);
      socket.off("chat:participant-removed", handleParticipantRemoved);
      socket.off("chat:group-name-updated", handleGroupNameUpdated);
      socket.off("chat:message-status-update", handleMessageStatusUpdate);
    };
  }, [loginUser?.id, dispatch]);

  return (
    <audio
      ref={audioRef}
      src="/chat-notify.wav"
      preload="auto"
      style={{ display: "none" }}
    />
  );
}
